/**
 * AI Image Generation Service (using POS AI Backend)
 * Calls the pos-ai Express.js service instead of directly calling Azure OpenAI
 * 
 * The backend handles:
 * - Azure OpenAI API calls
 * - API key security
 * - Prompt enhancement based on image type
 * - Error handling and logging
 */

import axiosInstance from './interceptor';

// AI Backend Configuration (for text generation)
const AI_API_URL = process.env.REACT_APP_AI_API_URL || 'https://pos-ai.dreamstechnologies.com';

// Available DALL·E sizes
export type DallESize = '1024x1024' | '1792x1024' | '1024x1792';

// Supported image generation modes
export type ImageType = 'banner' | 'image' | 'icon';

// Quality levels for DALL·E images
export type DalleQuality = 'standard' | 'hd';

// Style options for DALL·E images
export type DalleStyle = 'vivid' | 'natural';

// Content types for text generation
export type ContentType =
    | 'website_description'
    | 'product_description'
    | 'hero_section'
    | 'about_us'
    | 'testimonial'
    | 'review'
    | 'faq'
    | 'call_to_action'
    | 'tagline'
    | 'meta_description'
    | 'blog_post'
    | 'announcement'
    | 'feature_highlight'
    | 'newsletter'
    | 'email_subject'
    | 'social_media_post'
    | 'terms_and_conditions'
    | 'privacy_policy'
    | 'general'
    | 'mobile_app_description';

// Core config for image generation requests
export interface ImageGenerationConfig {
    prompt: string; // Base prompt (user or dynamic input)
    size?: DallESize; // Output size (optional, can be calculated from width/height)
    width?: number; // Desired width in pixels
    height?: number; // Desired height in pixels
    quality?: DalleQuality; // Optional quality flag (default: 'standard')
    style?: DalleStyle; // Optional style (default: 'vivid')
    type?: ImageType; // Optional image type, used for prompt enhancement
    negativePrompt?: string; // Optional exclusion terms (e.g., "no cartoon, no text")
    template?: string; // Optional template identifier
}

export interface AIImageResult {
    url: string;
    revised_prompt?: string;
}

// Text generation configuration
export interface TextGenerationConfig {
    prompt: string;
    type?: ContentType;
    context?: string;
    storeName?: string;
    location?: string;
    maxTokens?: number;
    temperature?: number;
}

export interface AITextResult {
    content: string;
    usage?: {
        prompt_tokens: number;
        completion_tokens: number;
        total_tokens: number;
    };
}

/**
 * Generate an image using the POS AI backend service
 * The backend will enhance the prompt based on the image type
 */
export const generateImage = async (
    config: ImageGenerationConfig
): Promise<AIImageResult> => {
    try {
        const endpoint = '/gemini-ai-image';
        console.log('🎨 Generating AI Image:', {
            endpoint,
            prompt: config.prompt.substring(0, 50) + '...',
            type: config.type,
            size: config.size,
            quality: config.quality || 'standard',
        });

        const response = await axiosInstance.post(endpoint, config);

        const data = response.data;

        if (!data.success) {
            throw new Error(data.error || 'Failed to generate image');
        }

        console.log('✅ AI Image generated successfully:', data.data.url.substring(0, 50) + '...');

        return {
            url: data.data.url,
            revised_prompt: data.data.revised_prompt,
        };
    } catch (error: any) {
        console.error('❌ AI Service Error:', error.message || error);

        // Provide user-friendly error messages
        if (error.message?.includes('Failed to fetch') || error.message?.includes('Network error')) {
            throw new Error('Cannot connect to AI service. Please ensure the AI service is running.');
        }

        // Extract error message from axios error response
        const errorMessage = error?.data?.error || error?.response?.data?.error || error?.message || 'Failed to generate image';
        throw new Error(errorMessage);
    }
};

/**
 * Get optimal DALL-E size based on dimensions and type
 */
export const getOptimalSize = (
    width: number,
    height: number,
    type: ImageType
): DallESize => {
    // Icons are always square
    if (type === 'icon') {
        return '1024x1024';
    }

    const aspectRatio = width / height;

    // Landscape (banner)
    if (aspectRatio > 1.2) {
        return '1792x1024';
    }
    // Portrait
    else if (aspectRatio < 0.8) {
        return '1024x1792';
    }
    // Square or close to square
    else {
        return '1024x1024';
    }
};

/**
 * Build enhanced prompt based on image type and configuration
 * @deprecated This is now handled by the backend. Use generateImage with type parameter instead.
 * Kept for backward compatibility only.
 */
export const buildPrompt = (config: ImageGenerationConfig): string => {
    const { prompt, type, negativePrompt } = config;

    const enhancements: Record<ImageType, string> = {
        banner: `Ultra-realistic professional website hero banner photograph, wide cinematic aspect ratio 16:9 composition,
    studio-quality lighting with soft diffused highlights and natural shadows, professional color grading with vibrant yet natural tones,
    featuring authentic real people in natural poses or premium real products in elegant arrangements,
    carefully balanced composition with intentional negative space on left or right side for text/CTA overlay placement,
    depth of field bokeh effect for professional look, commercial photography standard, high-end advertising quality,
    suitable for modern e-commerce, corporate, or lifestyle website hero section,
    absolutely no cartoon elements, no illustrations, no graphic overlays, no text, no UI elements, no buttons, pure photography only,
    shot on professional camera with 50mm lens, 8K resolution quality, ${prompt}`,
        icon: `${prompt}, clean minimal design, icon style, simple and recognizable, suitable for web or app use`,
        image: `${prompt}, professional quality, high resolution, suitable for general use`
    };

    const basePrompt = type ? enhancements[type] : prompt;
    return negativePrompt ? `${basePrompt}, ${negativePrompt}` : basePrompt;
};

/**
 * Enhance prompt based on image type for professional UI/UX quality
 * @deprecated Prompt enhancement is now handled by the backend.
 * Use generateImage with type parameter instead. Kept for backward compatibility only.
 */
export const enhancePrompt = (prompt: string, type: ImageType): string => {
    console.warn('⚠️ enhancePrompt is deprecated. Prompt enhancement is handled by backend.');
    return buildPrompt({ prompt, size: '1024x1024', type });
};

/**
 * Generate text content using the POS AI backend service
 * The backend will enhance the prompt based on the content type
 */
export const generateText = async (
    config: TextGenerationConfig
): Promise<AITextResult> => {
    try {
        const endpoint = `${AI_API_URL}/api/ai/generate-text`;
        console.log('✍️ Generating AI Text:', {
            endpoint,
            prompt: config.prompt.substring(0, 50) + '...',
            type: config.type || 'general',
        });

        const response = await fetch(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(config),
        });

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({ error: 'Network error' }));
            throw new Error(errorData.error || `HTTP ${response.status}: Failed to generate text`);
        }

        const data = await response.json();

        if (!data.success) {
            throw new Error(data.error || 'Failed to generate text');
        }

        console.log('✅ AI Text generated successfully');

        return {
            content: data.data.content,
            usage: data.data.usage,
        };
    } catch (error: any) {
        console.error('❌ AI Text Service Error:', error.message || error);

        // Provide user-friendly error messages
        if (error.message.includes('Failed to fetch') || error.message.includes('Network error')) {
            throw new Error('Cannot connect to AI service. Please ensure the AI service is running on port 4000.');
        }

        throw error;
    }
};

/**
 * Validate AI Service Configuration
 */
export const validateAPIKey = (): boolean => {
    return !!AI_API_URL;
};

export default {
    generateImage,
    generateText,
    getOptimalSize,
    buildPrompt,
    enhancePrompt,
    validateAPIKey,
    AI_API_URL,
};
